import { useState, useEffect } from 'react';
import { api } from '../../api';
import type { AuditFinding, BusinessContext, Suggestion, SuggestionCategory, SuggestionImpact, SuggestionStatus } from '../../types';
import { CopyForLlmButton } from '../../components/CopyForLlmButton';

interface Props {
  domainId: string;
  domain: string;
  onNavigateToFaqGenerator?: (targetUrl?: string) => void;
  onNavigateToOutreachPipeline?: () => void;
  onNavigateToContentKeywords?: () => void;
}

const CATEGORY_LABELS: Record<SuggestionCategory, string> = {
  geo: 'GEO',
  seo: 'SEO',
  authority: 'Authority',
  technical: 'Technical',
  aeo: 'AEO',
  directory: 'Directory',
  content: 'Content',
};

const CATEGORY_COLORS: Record<SuggestionCategory, string> = {
  geo:       'bg-blue-100 text-blue-700',
  seo:       'bg-purple-100 text-purple-700',
  authority: 'bg-amber-100 text-amber-700',
  technical: 'bg-gray-100 text-gray-700',
  aeo:       'bg-emerald-100 text-emerald-700',
  directory: 'bg-blue-100 text-blue-700',
  content:   'bg-teal-100 text-teal-700',
};

const IMPACT_COLORS: Record<SuggestionImpact, string> = {
  very_high: 'bg-red-50 text-red-600',
  high:      'bg-orange-50 text-orange-600',
  medium:    'bg-amber-50 text-amber-600',
  low:       'bg-gray-50 text-gray-500',
};

const IMPACT_LABELS: Record<SuggestionImpact, string> = {
  very_high: 'Very high',
  high:      'High',
  medium:    'Medium',
  low:       'Low',
};

const DIFFICULTY_LABELS: Record<string, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
};

function actionButtonClass(impact: SuggestionImpact): string {
  if (impact === 'very_high') return 'bg-brand-green text-white hover:bg-brand-green-dark';
  if (impact === 'high')      return 'border border-brand-green text-brand-green hover:bg-green-50';
  return 'border border-gray-300 text-gray-500 hover:bg-gray-50';
}

function actionButtonLabel(impact: SuggestionImpact, hasUrl: boolean): string {
  if (!hasUrl) return 'Learn more →';
  if (impact === 'very_high') return 'Do this now →';
  return 'Take action →';
}

// ── Dismiss modal ─────────────────────────────────────────────────────────────

function DismissModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
        <h3 className="font-semibold text-brand-ink mb-2">Dismiss suggestion</h3>
        <p className="text-sm text-gray-500 mb-4">Optional: tell us why you're dismissing this.</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Not relevant, already done, etc."
          className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-brand-green mb-4"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="text-sm text-gray-500 px-4 py-2 rounded-lg hover:text-brand-ink">Cancel</button>
          <button
            onClick={() => onConfirm(reason)}
            className="text-sm bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-brand-ink"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Suggestion card ───────────────────────────────────────────────────────────

function buildSuggestionPrompt(
  suggestion: Suggestion,
  domain: string,
  biz: BusinessContext | null,
): string {
  const lines: string[] = [
    `Domain: ${domain}`,
    `URL: https://${domain}`,
  ];
  if (biz) {
    lines.push(`Business: ${biz.coreOffering}${biz.coreOfferingCategory ? ` — ${biz.coreOfferingCategory}` : ''}`);
    lines.push(`Audience: ${biz.targetAudience}`);
    if (biz.geographyCity || biz.geographyCountry) {
      lines.push(`Location: ${[biz.geographyCity, biz.geographyCountry].filter(Boolean).join(', ')}`);
    }
  }
  lines.push('');
  lines.push(`Task: ${suggestion.title}`);
  lines.push(`Category: ${suggestion.category} | Impact: ${suggestion.impact} | Effort: ${suggestion.difficulty}`);
  lines.push('');
  lines.push('Why this was flagged:');
  lines.push(suggestion.why);
  return lines.join('\n');
}

function SuggestionCard({
  suggestion,
  domain,
  biz,
  onStatusChange,
  onMarkPreExisting,
  onNavigateToFaqGenerator,
  onNavigateToOutreachPipeline,
  onNavigateToContentKeywords,
}: {
  suggestion: Suggestion;
  domain: string;
  biz: BusinessContext | null;
  onStatusChange: (id: string, status: SuggestionStatus, reason?: string) => Promise<void>;
  onMarkPreExisting: (id: string) => Promise<void>;
  onNavigateToFaqGenerator?: (targetUrl?: string) => void;
  onNavigateToOutreachPipeline?: () => void;
  onNavigateToContentKeywords?: () => void;
}) {
  const [dismissing, setDismissing] = useState(false);
  const [updating, setUpdating] = useState(false);

  async function handleStatus(status: SuggestionStatus, reason?: string) {
    setUpdating(true);
    try {
      await onStatusChange(suggestion.id, status, reason);
    } finally {
      setUpdating(false);
    }
  }

  async function handleMarkPreExisting() {
    setUpdating(true);
    try {
      await onMarkPreExisting(suggestion.id);
    } finally {
      setUpdating(false);
    }
  }

  const statusBadge: Record<SuggestionStatus, string> = {
    active:       '',
    in_progress:  'bg-amber-50 text-amber-600',
    done:         'bg-green-50 text-brand-green',
    dismissed:    'bg-gray-100 text-gray-400',
    pre_existing: 'bg-blue-50 text-blue-500',
  };

  const statusLabel: Record<SuggestionStatus, string> = {
    active:       '',
    in_progress:  'In progress',
    done:         '✓ Done',
    dismissed:    'Dismissed',
    pre_existing: 'Already have this',
  };

  return (
    <>
      {dismissing && (
        <DismissModal
          onConfirm={(reason) => { setDismissing(false); void handleStatus('dismissed', reason); }}
          onCancel={() => setDismissing(false)}
        />
      )}
      <div className={`bg-white border rounded-xl p-5 shadow-sm transition-opacity ${suggestion.status === 'done' ? 'opacity-60' : ''}`}>
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {/* Badges row */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[suggestion.category]}`}>
                {CATEGORY_LABELS[suggestion.category]}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${IMPACT_COLORS[suggestion.impact]}`}>
                {IMPACT_LABELS[suggestion.impact]} impact
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {DIFFICULTY_LABELS[suggestion.difficulty] ?? suggestion.difficulty} effort
              </span>
              {suggestion.priority && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green font-medium">Priority</span>
              )}
              {suggestion.status !== 'active' && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge[suggestion.status]}`}>
                  {statusLabel[suggestion.status]}
                </span>
              )}
            </div>

            <h3 className="font-semibold text-brand-ink mb-1 text-sm">{suggestion.title}</h3>
            <p className="text-xs text-gray-500 leading-relaxed">{suggestion.why}</p>

            {/* Off-domain disclaimer */}
            {suggestion.cannot_auto_detect && suggestion.status === 'active' && (
              <p className="text-xs text-blue-500 mt-2 flex items-center gap-1">
                <span aria-hidden="true">&#9432;</span>
                We can&apos;t automatically detect this. Please check the platform yourself.
              </p>
            )}
          </div>

          {/* Action button */}
          {suggestion.status !== 'done' && (
            suggestion.suggestion_key === 'reach_out_for_links'
              ? onNavigateToOutreachPipeline !== undefined && (
                <button
                  onClick={onNavigateToOutreachPipeline}
                  className={`shrink-0 text-xs font-medium px-3 py-2 rounded-lg transition-colors ${actionButtonClass(suggestion.impact)}`}
                >
                  View targets →
                </button>
              )
              : (suggestion.suggestion_key === 'expand_faqs' || suggestion.suggestion_key === 'expand_faqs_more') && onNavigateToFaqGenerator
              ? (
                <button
                  onClick={() => onNavigateToFaqGenerator(suggestion.action_url ?? undefined)}
                  className={`shrink-0 text-xs font-medium px-3 py-2 rounded-lg transition-colors ${actionButtonClass(suggestion.impact)}`}
                >
                  Generate FAQs →
                </button>
              )
              : suggestion.suggestion_key === 'content_gap' && onNavigateToContentKeywords !== undefined
              ? (
                <button
                  onClick={onNavigateToContentKeywords}
                  className={`shrink-0 text-xs font-medium px-3 py-2 rounded-lg transition-colors ${actionButtonClass(suggestion.impact)}`}
                >
                  View Keywords →
                </button>
              )
              : suggestion.action_url && (
                <a
                  href={suggestion.action_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`shrink-0 text-xs font-medium px-3 py-2 rounded-lg transition-colors ${actionButtonClass(suggestion.impact)}`}
                >
                  {actionButtonLabel(suggestion.impact, true)}
                </a>
              )
          )}
        </div>

        {/* Status actions */}
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-gray-50 items-center">
          {suggestion.status !== 'done' && suggestion.status !== 'dismissed' && suggestion.status !== 'pre_existing' && (
            <>
              {suggestion.status !== 'in_progress' && (
                <button
                  onClick={() => handleStatus('in_progress')}
                  disabled={updating}
                  className="text-xs text-amber-600 hover:text-amber-700 px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
                >
                  Mark in progress
                </button>
              )}
              <button
                onClick={() => handleStatus('done')}
                disabled={updating}
                className="text-xs text-brand-green hover:text-brand-ink px-2 py-1 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
              >
                Mark as done
              </button>
              <button
                onClick={() => setDismissing(true)}
                disabled={updating}
                className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Dismiss
              </button>
              {suggestion.cannot_auto_detect && (
                <button
                  onClick={() => void handleMarkPreExisting()}
                  disabled={updating}
                  className="text-xs text-blue-500 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  I already have this
                </button>
              )}
            </>
          )}
          <span className="ml-auto">
            <CopyForLlmButton prompt={buildSuggestionPrompt(suggestion, domain, biz)} />
          </span>
        </div>
      </div>
    </>
  );
}

// ── Audit finding actions ─────────────────────────────────────────────────────

interface FindingAction {
  title: string;
  why: string;
  action: string;
}

const FINDING_ACTIONS: Record<string, FindingAction> = {
  // SEO
  'seo.https': { title: 'Enable HTTPS', why: 'HTTP sites are deprioritised by AI crawlers and search engines. Secure connections are a trust baseline.', action: 'Configure an SSL/TLS certificate on your server and redirect all HTTP traffic to HTTPS.' },
  'seo.mobile_viewport': { title: 'Add mobile viewport meta tag', why: 'Missing viewport meta causes browsers to render the desktop layout on mobile devices, which AI platforms that test mobile rendering may flag.', action: 'Add <meta name="viewport" content="width=device-width, initial-scale=1"> to your <head>.' },
  'seo.ai_bot_access': { title: 'Unblock AI crawlers in robots.txt', why: 'Your robots.txt is blocking AI-specific user agents (GPTBot, Google-Extended, CCBot, PerplexityBot), preventing AI platforms from indexing your site.', action: 'Add explicit Allow rules for GPTBot, Google-Extended, CCBot, and PerplexityBot in robots.txt, or remove blanket Disallow rules that catch these agents.' },
  'seo.title_tag': { title: 'Add or fix your page title tag', why: 'A missing or duplicate title tag is a primary signal AI platforms use when deciding whether to cite your page.', action: 'Write a unique, descriptive <title> (50–60 characters) for each key page that includes your primary keyword.' },
  'seo.meta_description': { title: 'Fix your meta description length', why: 'Your meta description is too long (over 160 characters) or missing. AI platforms and search engines truncate or ignore descriptions that are too long — they cannot use them as citation snippets reliably.', action: 'Rewrite your meta description to 140–160 characters. Summarise the page purpose and include your target phrase. Long descriptions are truncated; short ones may be ignored.' },
  'seo.single_h1': { title: 'Fix your H1 heading', why: 'Pages with no H1 or multiple H1 tags confuse AI parsers about the primary topic of the page.', action: 'Ensure every page has exactly one H1 that clearly states the page topic.' },
  'seo.canonical': { title: 'Add a canonical tag', why: 'Without a canonical tag, AI crawlers may index duplicate versions of your pages and split citation authority.', action: 'Add <link rel="canonical" href="https://yourdomain.com/page/"> to the <head> of every indexable page.' },
  'seo.og_tags': { title: 'Add Open Graph tags', why: 'Open Graph tags control how your content is previewed when AI platforms and social networks reference your pages.', action: 'Add og:title, og:description, og:image, and og:url meta tags to your page <head>.' },
  'seo.image_alt_coverage': { title: 'Add alt text to images', why: 'Images without alt text are invisible to AI text parsers, reducing the richness of content AI can index from your page.', action: 'Add descriptive alt attributes to all meaningful images. Decorative images should have alt="".' },
  'seo.cwv_lcp': { title: 'Improve Largest Contentful Paint (LCP)', why: 'Slow LCP signals poor user experience. Google AI Overview rankings weight Core Web Vitals.', action: 'Optimise your largest image or text element to load within 2.5 seconds: compress images, use a CDN, eliminate render-blocking resources.' },
  'seo.review_signals': { title: 'Build review platform presence', why: 'AI platforms use review aggregates (count, rating, recency) as trust proxies when deciding which businesses to cite.', action: 'Claim your Google Business Profile and Trustpilot listings. Actively request reviews from satisfied customers.' },
  // AEO
  'aeo.faq_schema': { title: 'Add FAQPage JSON-LD schema', why: 'FAQPage schema is the canonical signal AI platforms use to cite your Q&A pairs verbatim. CSS-accordion FAQ content is readable by crawlers but JSON-LD gives AI explicit permission to reproduce answers directly.', action: 'Wrap your existing FAQ pairs in FAQPage JSON-LD in your page <head>. Each entry uses Question with an acceptedAnswer. This is the single highest-leverage AEO change for a site that already has FAQ content.' },
  'aeo.faq_content': { title: 'Verify and expand FAQ content', why: 'The automated scan flagged this page as having few or no detectable FAQ pairs. Note: if your FAQs use a CSS accordion with button elements rather than native <details> tags, the scanner may have missed them — verify in page source before adding new content.', action: 'Check view-source for your FAQ questions and answers. If they are present in HTML, the next step is adding FAQPage JSON-LD schema (not adding more content). If FAQ content is genuinely missing, add 5+ Q&A pairs as server-rendered HTML targeting the questions your audience asks AI platforms.' },
  'aeo.structured_data': { title: 'Add or complete structured data', why: 'Structured data gives AI platforms machine-readable facts about your business. Without it, AI must infer everything from prose, which reduces citation accuracy.', action: 'Add Organization or LocalBusiness JSON-LD schema to your homepage. Include name, url, description, address, and telephone at minimum.' },
  'aeo.question_headings': { title: 'Rewrite section headings as questions', why: 'AI citation engines pattern-match on question-format H2/H3 headings (starting with "What", "How", "Why" etc.) as signals that your page directly answers user queries.', action: 'Rewrite your key section headings as natural questions your audience would type into an AI platform. Aim for 3–5 question headings per page.' },
  'aeo.heading_hierarchy': { title: 'Rewrite section headings as questions', why: 'AI citation engines pattern-match on question-format headings as signals that your page directly answers user queries.', action: 'Rewrite your key section headings (H2, H3) as natural questions your audience would ask. Aim for 3–5 per page.' },
  'aeo.direct_answer_opening': { title: 'Add a direct-answer opening paragraph', why: 'AI platforms use the first paragraph as a citation snippet. A direct answer in the opening increases your chance of being quoted verbatim.', action: 'Open your key pages with 1–2 sentences that directly answer the primary question the page is about, before any supporting detail.' },
  'aeo.article_schema': { title: 'Add Article or WebPage schema', why: 'Article schema signals that your content is a structured piece of knowledge — AI platforms prioritise content they can clearly classify.', action: 'Add Article or BlogPosting JSON-LD to content pages including datePublished, dateModified, author, and headline.' },
  'aeo.organization_schema': { title: 'Add Organisation schema', why: 'Organization schema is how AI platforms build their knowledge graph entry for your business. Without it, AI must infer your name, URL, and description from prose.', action: 'Add Organization JSON-LD to your homepage with name, url, description, logo, and sameAs links to your authoritative profiles.' },
  // GEO
  'geo.sameas_authority': { title: 'Add sameAs links to your schema', why: '"sameAs" links in your JSON-LD schema are the primary signal AI platforms use to confirm your business entity is real and well-established. Authoritative profiles dramatically increase citation probability.', action: 'Add a sameAs array to your Organization schema pointing to your profiles on Wikidata, Crunchbase, LinkedIn, and relevant industry directories where you have verified listings.' },
  'geo.directory_presence': { title: 'Build directory listings', why: 'AI platforms cross-reference business directories (Google Business Profile, Yelp, Trustpilot, industry verticals) to confirm entity legitimacy. More confirmed listings means higher citation confidence.', action: 'Claim and complete your profile on Google Business Profile, Bing Places, Yelp, and 2–3 industry-specific directories. Ensure your business name, address, and phone are identical across all.' },
  'geo.entity_consistency': { title: 'Standardise your business name everywhere', why: 'AI knowledge graphs merge entity data from multiple sources. Inconsistent business names create separate "entities" and split your citation authority.', action: 'Audit your business name across your website, all directory listings, and social profiles. Standardise to one canonical form and update any that differ.' },
  'geo.llms_txt_present': { title: 'Add an llms.txt file', why: 'llms.txt is an emerging standard that tells AI crawlers which content on your site is authoritative for AI citation. Early adopters gain a systematic advantage.', action: 'Create /llms.txt at your domain root listing your key pages and their topics. Follow the specification at llmstxt.org.' },
  'geo.about_page_depth': { title: 'Deepen your About page', why: 'AI platforms use About pages to extract entity facts — founders, year founded, location, mission. A thin About page means AI must guess these details.', action: 'Expand your About page to 300+ words covering: who you are, what you do, who you serve, where you operate, and when you were founded. Include team bios and a clear value proposition.' },
  'geo.review_platform_presence': { title: 'Build review platform presence', why: 'AI citation probability is correlated with review platform presence. AI platforms look for social proof signals before recommending a business.', action: 'Claim profiles on Google Business Profile, Trustpilot, G2, or Capterra (whichever suits your industry). Actively request reviews from satisfied customers.' },
  'geo.inbound_citation_velocity': { title: 'Build AI citation velocity', why: 'Your domain is not yet being cited in AI platform responses. AI platforms favour businesses already mentioned in authoritative content.', action: 'Publish expert content that answers specific questions in your domain, seek press mentions, build partnerships with complementary businesses, and ensure your structured data is complete so AI can confidently cite you.' },
};

const FINDING_PILLAR_COLORS: Record<string, string> = {
  seo: 'bg-purple-100 text-purple-700',
  aeo: 'bg-emerald-100 text-emerald-700',
  geo: 'bg-blue-100 text-blue-700',
};

const FINDING_IMPACT_COLORS: Record<string, string> = {
  high:   'bg-gray-800 text-white',
  medium: 'bg-gray-200 text-gray-700',
  low:    'bg-gray-100 text-gray-500',
};

function buildFindingPrompt(
  finding: AuditFinding,
  action: FindingAction,
  domain: string,
  biz: BusinessContext | null,
): string {
  const lines = [
    `Domain: ${domain}`,
    `URL: https://${domain}`,
  ];
  if (biz) {
    lines.push(`Business: ${biz.coreOffering}${biz.coreOfferingCategory ? ` — ${biz.coreOfferingCategory}` : ''}`);
    lines.push(`Audience: ${biz.targetAudience}`);
    if (biz.geographyCity || biz.geographyCountry) {
      lines.push(`Location: ${[biz.geographyCity, biz.geographyCountry].filter(Boolean).join(', ')}`);
    }
  }
  lines.push('');
  lines.push(`Issue: ${action.title}`);
  lines.push(`Pillar: ${finding.pillar.toUpperCase()} | Impact: ${finding.impactBand} | Check: ${finding.checkId}`);
  lines.push('');
  lines.push('Why this matters:');
  lines.push(action.why);
  lines.push('');
  lines.push('Suggested action:');
  lines.push(action.action);
  return lines.join('\n');
}

function AuditFindingCard({
  finding,
  domain,
  biz,
}: {
  finding: AuditFinding;
  domain: string;
  biz: BusinessContext | null;
}) {
  const action = FINDING_ACTIONS[finding.checkId];
  if (!action) return null;

  const pillarLabel = finding.pillar.toUpperCase();
  const pillarColor = FINDING_PILLAR_COLORS[finding.pillar] ?? 'bg-gray-100 text-gray-600';
  const impactColor = FINDING_IMPACT_COLORS[finding.impactBand] ?? FINDING_IMPACT_COLORS['medium'];
  const isWarning = finding.verdict === 'warning';

  return (
    <div className={`bg-white border rounded-xl p-5 shadow-sm ${isWarning ? 'border-amber-200' : 'border-red-200'}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pillarColor}`}>{pillarLabel}</span>
            <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${impactColor}`}>{finding.impactBand}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isWarning ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
              {isWarning ? 'Warning' : 'Fail'}
            </span>
          </div>
          <h3 className="font-semibold text-brand-ink mb-1 text-sm">{action.title}</h3>
          <p className="text-xs text-gray-500 leading-relaxed mb-2">{action.why}</p>
          <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 rounded-lg px-3 py-2">{action.action}</p>
        </div>
      </div>
      <div className="flex justify-end mt-3 pt-3 border-t border-gray-50">
        <CopyForLlmButton prompt={buildFindingPrompt(finding, action, domain, biz)} />
      </div>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

export function SuggestionsTab({ domainId, domain, onNavigateToFaqGenerator, onNavigateToOutreachPipeline, onNavigateToContentKeywords }: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [bizContext, setBizContext] = useState<BusinessContext | null>(null);
  const [auditFindings, setAuditFindings] = useState<AuditFinding[]>([]);

  useEffect(() => {
    Promise.all([
      api.domains.getSuggestions(domainId),
      api.domains.getBusinessContext(domainId).catch((): null => null),
      api.domains.getAudits(domainId)
        .then(async (res) => {
          const latest = res.audits[res.audits.length - 1];
          if (!latest) return [] as AuditFinding[];
          const findingsRes = await api.audits.getFindings(latest.auditId);
          return findingsRes.findings;
        })
        .catch((): AuditFinding[] => []),
    ])
      .then(([sugs, biz, findings]) => {
        setSuggestions(sugs);
        setBizContext(biz);
        setAuditFindings(findings);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load suggestions'))
      .finally(() => setLoading(false));
  }, [domainId]);

  async function handleStatusChange(id: string, status: SuggestionStatus, reason?: string) {
    const res = await api.suggestions.updateStatus(id, status, reason);
    setSuggestions((prev) =>
      prev.map((s) => s.id === id ? { ...s, status, dismissal_reason: reason ?? null } : s),
    );
    if (status === 'done' && res.triggerKind === 'targeted_audit') {
      setToast('Re-validating your site...');
      setTimeout(() => setToast(''), 4000);
    }
  }

  async function handleMarkPreExisting(id: string) {
    await api.suggestions.markPreExisting(id);
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) return <p className="text-sm text-red-500">{error}</p>;

  const active = suggestions.filter((s) => s.status === 'active' || s.status === 'in_progress');
  const done = suggestions.filter((s) => s.status === 'done');
  const dismissed = suggestions.filter((s) => s.status === 'dismissed');
  const highImpact = active.filter((s) => s.impact === 'very_high' || s.impact === 'high');

  // Failing/warning findings that have a known action and are not already in active suggestions
  const activeSuggestionWhys = new Set(active.map((s) => s.suggestion_key));
  const CHECK_TO_SUGGESTION_KEY: Record<string, string> = {
    'seo.https': 'fix_https', 'seo.mobile_viewport': 'fix_mobile_viewport',
    'seo.ai_bot_access': 'fix_robots_txt', 'seo.title_tag': 'fix_title_tag',
    'seo.meta_description': 'fix_meta_description', 'seo.single_h1': 'fix_h1',
    'seo.canonical': 'fix_canonical', 'seo.og_tags': 'fix_og_tags',
    'seo.image_alt_coverage': 'fix_image_alt',
    'aeo.faq_schema': 'fix_faq_schema', 'aeo.faq_content': 'expand_faqs',
    'aeo.question_headings': 'rewrite_headings_as_questions',
    'aeo.heading_hierarchy': 'rewrite_headings_as_questions',
    'aeo.structured_data': 'fix_organization_schema',
    'aeo.organization_schema': 'fix_organization_schema',
    'geo.sameas_authority': 'add_sameas_links',
    'geo.directory_presence': 'get_directory_listings',
    'geo.entity_consistency': 'fix_entity_consistency',
    'geo.llms_txt_present': 'add_llms_txt',
    'geo.about_page_depth': 'improve_about_page',
    'geo.review_platform_presence': 'get_review_platforms',
  };
  const failingFindings = auditFindings
    .filter((f) => (f.verdict === 'fail' || f.verdict === 'warning') && FINDING_ACTIONS[f.checkId])
    .filter((f) => {
      const mappedKey = CHECK_TO_SUGGESTION_KEY[f.checkId];
      return !mappedKey || !activeSuggestionWhys.has(mappedKey);
    })
    .sort((a, b) => {
      const ord = { high: 0, medium: 1, low: 2 };
      return (ord[a.impactBand] ?? 1) - (ord[b.impactBand] ?? 1);
    });

  if (suggestions.length === 0 && failingFindings.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
        <p className="text-gray-500 text-sm mb-1">No suggestions yet</p>
        <p className="text-xs text-gray-400">Run an audit from the Overview tab to generate your personalised action list.</p>
      </div>
    );
  }

  // Group active suggestions by category
  const CATEGORY_ORDER: SuggestionCategory[] = ['geo', 'authority', 'seo', 'technical', 'content'];
  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: active.filter((s) => s.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <p className="text-xs text-brand-green font-medium">{toast}</p>
      )}

      {/* Summary bar */}
      <div className="bg-brand-ink text-brand-paper rounded-xl px-5 py-3 text-sm flex items-center justify-between">
        <span>
          {highImpact.length > 0
            ? `${highImpact.length} high-impact action${highImpact.length !== 1 ? 's' : ''} pending`
            : `${active.length} suggestion${active.length !== 1 ? 's' : ''} pending`}
        </span>
        {suggestions[0]?.updated_at && (
          <span className="text-gray-400 text-xs">
            Updated {new Date(suggestions[0].updated_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Failing audit checks */}
      {failingFindings.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-brand-ink">Audit findings requiring action</h2>
            <span className="text-xs text-gray-400">{failingFindings.length} check{failingFindings.length !== 1 ? 's' : ''} failed</span>
          </div>
          <div className="space-y-3">
            {failingFindings.map((f) => (
              <AuditFindingCard key={f.checkId} finding={f} domain={domain} biz={bizContext} />
            ))}
          </div>
        </div>
      )}

      {/* Active groups */}
      {grouped.map(({ cat, items }) => (
        <div key={cat}>
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[cat]}`}>
              {CATEGORY_LABELS[cat]}
            </span>
            <span className="text-xs text-gray-400">{items.length} pending</span>
          </div>
          <div className="space-y-3">
            {items.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                domain={domain}
                biz={bizContext}
                onStatusChange={handleStatusChange}
                onMarkPreExisting={handleMarkPreExisting}
                {...(onNavigateToFaqGenerator !== undefined ? { onNavigateToFaqGenerator } : {})}
                {...(onNavigateToOutreachPipeline !== undefined ? { onNavigateToOutreachPipeline } : {})}
                {...(onNavigateToContentKeywords !== undefined ? { onNavigateToContentKeywords } : {})}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Done section */}
      {done.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-3">Completed ({done.length})</p>
          <div className="space-y-2">
            {done.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                domain={domain}
                biz={bizContext}
                onStatusChange={handleStatusChange}
                onMarkPreExisting={handleMarkPreExisting}
                {...(onNavigateToFaqGenerator !== undefined ? { onNavigateToFaqGenerator } : {})}
                {...(onNavigateToOutreachPipeline !== undefined ? { onNavigateToOutreachPipeline } : {})}
                {...(onNavigateToContentKeywords !== undefined ? { onNavigateToContentKeywords } : {})}
              />
            ))}
          </div>
        </div>
      )}

      {/* Dismissed */}
      {dismissed.length > 0 && (
        <details className="group">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            Dismissed ({dismissed.length})
          </summary>
          <div className="space-y-2 mt-2">
            {dismissed.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                domain={domain}
                biz={bizContext}
                onStatusChange={handleStatusChange}
                onMarkPreExisting={handleMarkPreExisting}
                {...(onNavigateToFaqGenerator !== undefined ? { onNavigateToFaqGenerator } : {})}
                {...(onNavigateToOutreachPipeline !== undefined ? { onNavigateToOutreachPipeline } : {})}
                {...(onNavigateToContentKeywords !== undefined ? { onNavigateToContentKeywords } : {})}
              />
            ))}
          </div>
        </details>
      )}

      {/* All done empty state */}
      {active.length === 0 && done.length > 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">You've addressed all current suggestions.</p>
          <p className="text-xs text-gray-400 mt-1">Your next audit will surface new recommendations.</p>
        </div>
      )}
    </div>
  );
}
